import AdvancedTransactionTypes from "const/AdvancedTransactionTypes";
import Arrays from "utils/Arrays";
import Constants from "const/Constants";
import ContactSubTypes from "const/ContactSubTypes";
import Countries from "const/Countries";
import DataStatuses from "const/DataStatuses";
import Dates from "utils/Dates";
import DocumentPaymentTypes from "const/DocumentPaymentTypes";
import DocumentTypes from "const/DocumentTypes";
import Documents from "utils/Documents";
import EditDocumentConstants from "const/EditDocumentConstants";
import Money from "utils/Money";
import Objects from "utils/Objects";

const { COLORED_BLOCKS, COLOR_SET, FIELD_COLORS } = EditDocumentConstants;

const TOP_COORD_MULTIPLIER = 0.99982;

const PAGES_BOTTOM_MARGIN = 8;

export default class EditDocument {
  static getColoredBlocks(ocrMeta, { showDueDate = false } = {}) {
    return [...COLORED_BLOCKS.map((name) => {
      if (name === "dueDate" && !showDueDate) {
        return null;
      }

      return ocrMeta[name]?.boundingBox ? {
        color: FIELD_COLORS[name],
        block: {
          id: name,
          boundingBox: ocrMeta[name]?.boundingBox
        },
        sourceFieldId: name
      } : null;
    }).filter(Boolean), ...Object.entries(ocrMeta.lineItems || {}).reduce((result, [uuid, lineItem]) => {
      return [...result, ...Object.entries(lineItem).reduce((fields, [name, field]) => {
        return [...fields, ...(field?.boundingBoxes || (field?.boundingBox ? [field.boundingBox] : []))
          .map((boundingBox, index) => ({
            color: EditDocument.getColorFromFieldId(`${name}.${uuid}`),
            block: {
              id: `${name}.${uuid}.${index}`,
              boundingBox
            },
            sourceFieldId: `${name}.${uuid}`
          }))];
      }, [])];
    }, [])];
  }

  static getColoredBlocksToDisplay(coloredBlocks, activeField, hoveredField) {
    return coloredBlocks.filter((block) => {
      return (
        !activeField && !hoveredField && !block.sourceFieldId.includes("."))
        || block.sourceFieldId === activeField?.id
        || block.sourceFieldId === hoveredField?.id;
    });
  }

  static getColorFromFieldId(fieldId) {
    const numb = [...fieldId, "2"].reduce((aggregator, char) => {
      return aggregator + char.charCodeAt(0);
    }, 0);

    return COLOR_SET[numb % COLOR_SET.length];
  }

  static getBlockRenderCoords(
    boundingBox,
    { docPageHeights, docRenderWidth, docPageOffsets = [] }
  ) {
    if (!boundingBox) return null;

    const pageHeight = docPageHeights[boundingBox.page] || 0;

    const { left: pageLeftOffset = 0, top: pageTopOffset = 0 } = docPageOffsets[boundingBox.page] || {};

    let pageOffset = 0;

    // eslint-disable-next-line no-loops/no-loops
    for (let index = 0; index < boundingBox.page; ++index) {
      pageOffset += (docPageHeights[index] || 0) + PAGES_BOTTOM_MARGIN;
    }

    const left = pageLeftOffset + (boundingBox.left * docRenderWidth);

    const top = pageOffset + pageTopOffset + (boundingBox.top * pageHeight);

    const height = boundingBox.height * pageHeight;

    const width = boundingBox.width * docRenderWidth;

    return { left, top: top * TOP_COORD_MULTIPLIER, height, width };
  }

  static getPageViewportOffset({ view = [], rotate = 0, scale = 1 }) {
    const [left = 0, top = 0] = view;

    const normalizedRotate = (((rotate % Constants.CIRCLE_DEGREES) + Constants.CIRCLE_DEGREES) % Constants.CIRCLE_DEGREES);

    if (
      !left
      && top
      && [
        Constants.QUARTER_CIRCLE_DEGREES,
        Constants.THREE_QUARTERS_CIRCLE_DEGREES
      ].includes(normalizedRotate)
    ) {
      return {
        left: 0,
        top: 0
      };
    }
    switch (normalizedRotate) {
      case Constants.QUARTER_CIRCLE_DEGREES:
        return {
          left: -top * scale,
          top: -left * scale
        };
      case Constants.HALF_CIRCLE_DEGREES:
        return {
          left: left * scale,
          top: -top * scale
        };
      case Constants.THREE_QUARTERS_CIRCLE_DEGREES:
        return {
          left: top * scale,
          top: left * scale
        };
      case 0:
      default:
        return {
          left: -left * scale,
          top: top * scale
        };
    }
  }

  static normalizeAngle(angle) {
    return Math.round((Constants.CIRCLE_DEGREES - angle) / Constants.QUARTER_CIRCLE_DEGREES)
      * Constants.QUARTER_CIRCLE_DEGREES % Constants.CIRCLE_DEGREES;
  }

  static searchContact(contactsData, { id, idNumber, vatId, name }, { paymentType, integrationService, xeroBusiness }) {
    const buyPaymentType = paymentType === DocumentPaymentTypes.BUY;

    const contactData = contactsData.find((contact) => {
      if (paymentType && integrationService && !xeroBusiness) {
        if (
          (buyPaymentType && contact.subType !== ContactSubTypes.VENDOR)
        || (!buyPaymentType && contact.subType !== ContactSubTypes.CUSTOMER)
        ) {
          return false;
        }
      }
      if (id && id === contact.id) return true;

      return Objects.checkSimilarityBy({ idNumber, vatId, name }, contact);
    });

    return contactData;
  }

  static toMinMax(rect) {
    const { minX, maxX } = rect.start.x <= rect.end.x
      ? { minX: rect.start.x, maxX: rect.end.x }
      : { minX: rect.end.x, maxX: rect.start.x };

    const { minY, maxY } = rect.start.y <= rect.end.y
      ? { minY: rect.start.y, maxY: rect.end.y }
      : { minY: rect.end.y, maxY: rect.start.y };

    return { minX, maxX, minY, maxY };
  }

  static recalculateLineItemFields(prev, fieldName, value, extraData = {}) {
    const { xeroBusiness, classes = [], taxRates = [], currency } = extraData;

    const precision = Money.getCurrencyPrecision(currency);

    const useSelectedTaxRate = typeof prev.taxRate?.rate === "number" && (
      !prev.taxRate.rateDetails || (prev.taxRate.purchaseTaxRate === 0 && prev.taxRate.salesTaxRate === 0)
    );

    switch (fieldName) {
      case "amountTotal": {
        const vatRate = useSelectedTaxRate
          ? prev.taxRate.rate : (prev.taxRate?.rate || prev.vatRate || 0);

        const amountBase = vatRate
          ? Money.toMoneyNumber((value || 0) / (1 + ((vatRate || 0) / Constants.PERCENTS_MULTIPLIER)), true, precision)
          : value;

        const amountVat = vatRate ? Money.toMoneyNumber((value || 0) - amountBase, true, precision) : 0;

        return {
          ...prev,
          [fieldName]: value,
          vatRate: prev.taxRate?.rateDetails && !useSelectedTaxRate ? prev.vatRate : vatRate,
          amountVat,
          amountBase
        };
      }
      case "amountBase": {
        const vatRate = useSelectedTaxRate
          ? prev.taxRate.rate : (prev.vatRate || 0);

        const amountVat = value && vatRate
          ? Money.toMoneyNumber(value * vatRate / Constants.PERCENTS_MULTIPLIER, true, precision) : 0;

        const amountTotal = Money.toMoneyNumber((value || 0) + (amountVat || 0), true, precision);

        return { ...prev, [fieldName]: value, vatRate, amountVat, amountTotal };
      }
      case "amountVat": {
        const { amountBase } = prev;

        const vatRate = value && amountBase ? Money.toMoneyNumber(value / amountBase * Constants.PERCENTS_MULTIPLIER) : 0;

        const amountTotal = Money.toMoneyNumber((amountBase || 0) + (value || 0), true, precision);

        return { ...prev, [fieldName]: value, amountTotal, vatRate, amountBase };
      }
      case "vatRate": {
        const { amountBase } = prev;

        const amountVat = value && amountBase
          ? Money.toMoneyNumber(amountBase * value / Constants.PERCENTS_MULTIPLIER, true, precision)
          : 0;

        const amountTotal = Money.toMoneyNumber((amountBase || 0) + (amountVat || 0), true, precision);

        return { ...prev, [fieldName]: value, amountVat, amountBase, amountTotal };
      }
      case "taxRate": {
        const { amountBase } = prev;

        if (value?.id) {
          const amountVat = amountBase
            ? Money.toMoneyNumber(amountBase * value.rate / Constants.PERCENTS_MULTIPLIER, true, precision)
            : 0;

          const amountTotal = Money.toMoneyNumber((amountBase || 0) + (amountVat || 0), true, precision);

          return { ...prev, [fieldName]: value, vatRate: value.rate, amountVat, amountTotal };
        }

        return { ...prev, [fieldName]: value, vatRate: 0, amountVat: 0, amountTotal: amountBase };
      }
      case "category": {
        const taxRate = value?.taxType && Arrays.findById(taxRates, value.taxType);

        return {
          ...(taxRate && !taxRate.rateDetails && typeof taxRate.rate === "number"
            ? EditDocument.recalculateLineItemFields(prev, "taxRate", taxRate, extraData) : prev),
          [fieldName]: value,
          item: xeroBusiness ? prev.item : undefined,
          taxRate: taxRate || prev.taxRate
        };
      }
      case "item":
        return {
          ...prev,
          [fieldName]: value,
          category: xeroBusiness ? prev.category : undefined,
          class: (value?.classId && Arrays.findById(classes, value.classId)) || prev.class
        };
      default:
        return { ...prev, [fieldName]: value };
    }
  }

  static updateState(update, state, extraData) {
    const {
      recogniseData,
      countryCode,
      quickBooksBusiness,
      xeroBusiness,
      integrationService,
      contactsData,
      categories,
      status
    } = extraData;

    const keysToUpdate = Object.keys(update);

    const { lineItems, amountVat, amountBase, currency, paymentType, paymentDate, dueDate, issueDate, paid } = state;

    const precision = Money.getCurrencyPrecision(update.currency || currency);

    const usCountry = countryCode === Countries.US;

    keysToUpdate.forEach((key) => {
      switch (key) {
        case "amountBase": {
          if (lineItems.length === 1) {
            const lineItem = EditDocument.recalculateLineItemFields(
              lineItems[0],
              "amountBase",
              Money.toMoneyNumber(update.amountBase || 0, true, "...."),
              { xeroBusiness, currency }
            );

            update.lineItems = [lineItem];
            update.amountVat = lineItem.amountVat;
            update.amount = lineItem.amountTotal;
          } else {
            update.amount = Money.toMoneyNumber((update.amountBase || 0) + (amountVat || 0), true, precision);
          }
          break;
        }
        case "amountVat": {
          update.amount = Money.toMoneyNumber((update.amountVat || 0) + (amountBase || 0), true, precision);
          if (lineItems.length === 1) {
            const lineItem = EditDocument.recalculateLineItemFields(
              lineItems[0],
              "amountVat",
              Money.toMoneyNumber(update.amountVat || 0, true, precision),
              { xeroBusiness, currency }
            );

            update.lineItems = [lineItem];
            update.amountBase = lineItem.amountBase;
            update.amountTotal = lineItem.amountTotal;
          }
          break;
        }
        case "amount": {
          if (lineItems.length === 1) {
            const lineItem = EditDocument.recalculateLineItemFields(
              lineItems[0],
              "amountTotal",
              Money.toMoneyNumber(update.amount || 0, true, "...."),
              { xeroBusiness, currency }
            );

            update.lineItems = [lineItem];
            update.amountVat = lineItem.amountVat;
            update.amountBase = Money.toMoneyNumber(
              Math.max((update.amount || 0) - (update.amountVat || 0), 0), true, precision
            );
          } else {
            update.amountBase = Money.toMoneyNumber(
              Math.max((update.amount || 0) - (state.amountVat || 0), 0), true, precision
            );
          }
          break;
        }
        case "issueDate": {
          if (paid) update.paymentDate = update.issueDate;
          break;
        }
        case "payTo": {
          if (update.payTo?.serviceAccountId) {
            update.paid = true;
            if (!paymentDate && (dueDate || issueDate)) {
              update.paymentDate = Dates.formatApiDate(dueDate || issueDate);
            }
          }
          break;
        }
        case "exportAs": {
          update.paid = update.exportAs === AdvancedTransactionTypes.EXPENSE;
          update.payTo = {};
          break;
        }
        case "detailedTaxRates": {
          update.amountVat = update.detailedTaxRates
            .reduce((aggregator, currentValue) => aggregator + +(currentValue.value || 0), 0);
          update.amount = Money.toMoneyNumber((update.amountVat || 0) + (amountBase || 0), true, precision);
          break;
        }
        case "address": {
          update.vendorId = update.address.id;
          break;
        }
        case "paymentType": {
          const buyPaymentType = update.paymentType === DocumentPaymentTypes.BUY;

          const { payTo: { serviceAccountId, ...restPayTo } } = state;

          const { addresses: { recipient = {}, sender = {} } = {} } = recogniseData;

          const nextAddress = buyPaymentType ? sender : recipient;

          const contactSubType = buyPaymentType ? ContactSubTypes.VENDOR : ContactSubTypes.CUSTOMER;

          update.exportAs = quickBooksBusiness && update.type === DocumentTypes.RECEIPT ? AdvancedTransactionTypes.EXPENSE : "";
          update.paid = !!(quickBooksBusiness && update.type === DocumentTypes.RECEIPT);
          update.payTo = restPayTo;
          update.linkedDocumentsIds = [];
          if (status === DataStatuses.TO_REVIEW) {
            const contactData = EditDocument.searchContact(contactsData, nextAddress, {
              paymentType: update.paymentType,
              integrationService,
              xeroBusiness
            });

            update.address = contactData || { ...nextAddress, subType: contactSubType };
            update.vendorId = contactData ? contactData.id : undefined;
          }
          if (quickBooksBusiness && !usCountry) {
            const detailedTaxRates = Documents.getDetailedTaxRates(update.paymentType, lineItems, currency);

            update.detailedTaxRates = detailedTaxRates;
            update.amountVat = detailedTaxRates.reduce((aggregator, currentValue) => aggregator + currentValue.value || 0, 0);
          }
          if (xeroBusiness) {
            update.lineItems = lineItems.map((lineItem) => {
              const { purchaseItem, salesItem, purchaseCategoryCode, salesCategoryCode } = lineItem.item || {};

              if (buyPaymentType ? !purchaseItem : !salesItem) {
                return { ...lineItem, item: undefined };
              }

              const foundCategory = paymentType && Arrays.findByKey(
                categories,
                "code",
                buyPaymentType ? purchaseCategoryCode : salesCategoryCode
              );

              return foundCategory ? { ...lineItem, category: foundCategory } : lineItem;
            });
          }
          if ((countryCode === Countries.AU || countryCode === Countries.NZ) && buyPaymentType) {
            update.vatId = update.address?.vatId || "";
          }
          break;
        }
        case "lineItems": {
          const prevLineItemsAmounts = Documents.calculateLineItemsData({
            lineItems,
            paymentType,
            countryCode,
            quickBooksBusiness,
            currency
          });

          const lineItemsAmounts = Documents.calculateLineItemsData({
            lineItems: update.lineItems,
            paymentType,
            countryCode,
            quickBooksBusiness,
            currency
          });

          if (prevLineItemsAmounts.amount !== lineItemsAmounts.amount
            || prevLineItemsAmounts.taxesHash !== lineItemsAmounts.taxesHash) {
            update.amountBase = lineItemsAmounts.amountBase;
            update.amountVat = lineItemsAmounts.amountVat;
            update.amount = lineItemsAmounts.amount;
            update.tags = [
              ...new Set(
                lineItems.map(({ category, item }) => {
                  category = category || {};
                  item = item || {};

                  return category.code || item.id ? category.name || item.name : null;
                }).filter(Boolean)
              )
            ];
          }
          if (lineItemsAmounts.detailedTaxRates) {
            update.detailedTaxRates = lineItemsAmounts.detailedTaxRates;
          }
          break;
        }
        default:
          break;
      }
    });

    return update;
  }

  static rotateBoundingBox({ boundingBox, angle, width: origWidth, height: origHeight }) {
    const [rotatedWidth, rotatedHeight] = [
      Constants.QUARTER_CIRCLE_DEGREES,
      Constants.THREE_QUARTERS_CIRCLE_DEGREES
    ].includes(angle)
      ? [origHeight, origWidth] : [origWidth, origHeight];

    const half = 2;

    const xCoord = boundingBox.left * origWidth;

    const yCoord = boundingBox.top * origHeight;

    const width = boundingBox.width * origWidth;

    const height = boundingBox.height * origHeight;

    const points = [
      [xCoord, yCoord],
      [xCoord + width, yCoord],
      [xCoord, yCoord + height],
      [xCoord + width, yCoord + height]
    ];

    const centerX = origWidth / half;

    const centerY = origHeight / half;

    const angleRad = (angle * Math.PI) / Constants.HALF_CIRCLE_DEGREES;

    const rotatedPoints = points.map(([px, py]) => {
      const xNew = px - centerX;

      const yNew = py - centerY;

      const newX = (xNew * Math.cos(angleRad)) - (yNew * Math.sin(angleRad)) + centerX;

      const newY = (xNew * Math.sin(angleRad)) + (yNew * Math.cos(angleRad)) + centerY;

      return [newX, newY];
    });

    const minX = Math.min(...rotatedPoints.map(([point]) => point));

    const minY = Math.min(...rotatedPoints.map(([, point]) => point));

    const maxX = Math.max(...rotatedPoints.map(([point]) => point));

    const maxY = Math.max(...rotatedPoints.map(([, point]) => point));

    const newBoundingBox = {
      left: (minX - ((origWidth - rotatedWidth) / half)) / rotatedWidth,
      top: (minY - ((origHeight - rotatedHeight) / half)) / rotatedHeight,
      width: (maxX - minX) / rotatedWidth,
      height: (maxY - minY) / rotatedHeight,
      page: boundingBox.page
    };

    return newBoundingBox;
  }
}
